import authService from '../services/authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (reason?: any) => void;
}> = [];
let isRefreshing = false;

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((request) => {
    if (error) {
      request.reject(error);
      return;
    }

    if (token) {
      request.resolve(token);
    } else {
      request.reject(new Error('Token refresh failed'));
    }
  });

  failedQueue = [];
};

const isFormData = (value: any): value is FormData => {
  return typeof FormData !== 'undefined' && value instanceof FormData;
};

const isRefreshEndpoint = (endpoint: string): boolean => {
  return endpoint.includes('/api/v1/auth/refresh');
};

const buildHeaders = (accessToken?: string, payload?: any): Record<string, string> => {
  const headers: Record<string, string> = {};

  if (!isFormData(payload)) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return headers;
};

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = authService.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  try {
    const refreshResponse = await authService.refreshToken();
    const newToken = refreshResponse?.data?.accessToken;

    if (!refreshResponse?.success || !newToken) {
      throw new Error('Token refresh failed');
    }

    processQueue(null, newToken);
    return newToken;
  } catch (error) {
    processQueue(error, null);
    throw error;
  } finally {
    isRefreshing = false;
  }
};

const executeRequest = async <T = any>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  payload?: any,
  token?: string,
  hasRetried = false
): Promise<T> => {
  const accessToken = token || authService.getAccessToken() || undefined;
  const headers = buildHeaders(accessToken, payload);
  const requestBody = payload === undefined ? undefined : (isFormData(payload) ? payload : JSON.stringify(payload));

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers,
    body: requestBody,
  });

  return handleResponse<T>(response, {
    endpoint,
    canRefresh: Boolean(accessToken) && !hasRetried,
    retryFunction: () => executeRequest<T>(endpoint, method, payload, undefined, true),
  });
};

export const apiClient = {
  get: async <T = any>(endpoint: string, token?: string): Promise<T> => {
    return executeRequest<T>(endpoint, 'GET', undefined, token);
  },

  post: async <T = any>(endpoint: string, data: any, token?: string): Promise<T> => {
    return executeRequest<T>(endpoint, 'POST', data, token);
  },

  put: async <T = any>(endpoint: string, data: any, token?: string): Promise<T> => {
    return executeRequest<T>(endpoint, 'PUT', data, token);
  },

  delete: async <T = any>(endpoint: string, token?: string): Promise<T> => {
    return executeRequest<T>(endpoint, 'DELETE', undefined, token);
  },
};

const handleResponse = async <T = any>(
  response: Response,
  options: {
    endpoint: string;
    canRefresh: boolean;
    retryFunction?: () => Promise<T>;
  }
): Promise<T> => {
  const contentType = response.headers.get('content-type');
  let data: any;

  try {
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = { message: await response.text() };
    }
  } catch (_parseError) {
    data = { message: response.statusText || 'Request failed' };
  }

  if (response.status === 401 && options.canRefresh && !isRefreshEndpoint(options.endpoint)) {
    try {
      await refreshAccessToken();

      if (options.retryFunction) {
        return options.retryFunction();
      }

      throw new Error('No retry function available');
    } catch (error) {
      authService.logout();
      window.location.href = '/?auth=login';
      throw error;
    }
  }

  if (!response.ok) {
    const backendMessage =
      (data && (data.error?.message || data.error || data.message)) ||
      (typeof data === 'string' ? data : undefined);
    throw new Error(backendMessage || `HTTP error! status: ${response.status}`);
  }

  return data;
};
